import type {
  SgCsDirectorRow,
  SgCsShareholderRow,
  SgDirectorRow,
  SgIncorporationForm,
  SgNominatorInfo,
  SgOtherPositionHolder,
  SgPepRow,
  SgShareClass,
  SgShareCurrency,
  SgShareGroup,
  SgShareholderRow,
  SgUboRow,
} from '../types/sgIncorporation.types';
import { createEmptySgAddress } from '../utils/sgLocalAddress';
import { createEmptyPersonIdentityFiles, newFileCd } from '../../../utils/sgIncorporationFiles';

export function createEmptyCsDirector(): SgCsDirectorRow {
  return {
    no: '',
    name: '',
    nationality: '',
    identificationNo: '',
    residentialAddress: createEmptySgAddress(),
    dateOfAppointment: '',
  };
}

export function createEmptyCsShareholder(): SgCsShareholderRow {
  return {
    no: '',
    name: '',
    nationality: '',
    identificationNo: '',
    residentialAddress: createEmptySgAddress(),
    noOfSharesAndPercentage: '',
    dateOfAppointment: '',
  };
}

export function createEmptyDirector(): SgDirectorRow {
  const files = createEmptyPersonIdentityFiles();
  return {
    directorCd: files.personCd,
    directorName: '',
    directorAddress: createEmptySgAddress(),
    directorAlternateAddress: createEmptySgAddress(),
    directorIdType: '',
    directorIdentificationNo: '',
    directorNationality: '',
    directorDateOfBirth: '',
    directorEmail: '',
    directorTelCountryCode: '',
    directorTelAreaCode: '',
    directorTelNo: '',
    directorMobileCountryCode: '',
    directorMobileAreaCode: '',
    directorMobileNo: '',
    directorAreaCodeNotApplicable: false,
    directorIsPep: false,
    pepDetail: createEmptyPep(),
    appointmentEffectiveDate: '',
    executionDate: '',
    positions: ['LC_DIR'],
    isNominee: false,
    nominator: createEmptyNominator(),
    passportFile: null,
    proofOfAddressFile: null,
    passportFileCd: null,
    proofOfAddressFileCd: null,
  };
}

export function createEmptyShareholder(): SgShareholderRow {
  const files = createEmptyPersonIdentityFiles();
  return {
    shareholderCd: files.personCd,
    shareholderType: 'individual',
    name: '',
    nationality: '',
    idType: '',
    identificationNo: '',
    noOfShares: '',
    sharePercentage: '',
    shareType: 'ORD',
    heldInTrust: false,
    provideNameOfTrust: false,
    nameOfTrust: '',
    subClasses: [],
    allotments: [],
    isNominee: false,
    nominator: createEmptyNominator(),
    address: createEmptySgAddress(),
    dateOfBirth: '',
    email: '',
    mobileCountryCode: '',
    mobileAreaCode: '',
    mobileNo: '',
    isPep: false,
    pepDetail: createEmptyPep(),
    passportFile: null,
    proofOfAddressFile: null,
    passportFileCd: null,
    proofOfAddressFileCd: null,
    ihcName: '',
    ihcIndustry: '',
    ihcCountry: '',
    ihcIsPublic: false,
    ihcRegistrationNo: '',
    ihcStockExchange: '',
    ihcOwnershipStructure: '',
    upcName: '',
    upcIndustry: '',
    upcCountry: '',
    upcIsPublic: false,
    upcRegistrationNo: '',
    upcStockExchange: '',
    upcOwnershipStructure: '',
    ubos: [],
    csCompanyName: '',
    csRegistrationNo: '',
    csFormerName: '',
    csCountryOfIncorporation: '',
    csIncorporationDate: '',
    csBusinessActivityCode: '',
    csBusinessActivityName: '',
    csPaidUpCapital: '',
    csNoOfSharesIssued: '',
    csRegisteredAddress: createEmptySgAddress(),
    csLastFinancialYear: '',
    csWebsite: '',
    csIsPublicListed: false,
    csContactPerson: '',
    csContactOfficeCountryCode: '',
    csContactOfficeAreaCode: '',
    csContactNoOffice: '',
    csEmail: '',
    csMobileCountryCode: '',
    csMobileAreaCode: '',
    csMobileNo: '',
    csDirectors: [createEmptyCsDirector()],
    csShareholders: [createEmptyCsShareholder()],
  };
}

export function createEmptyShareClass(): SgShareClass {
  return { shareType: 'ORD', issuedCapAmount: '', paidupCapAmount: '', shareAllotted: '', subClasses: [] };
}

export function createEmptyShareCurrency(): SgShareCurrency {
  return {
    currency: 'SGD',
    payableMethod: 'CASH',
    allotmentMethod: '',
    allotmentMethodDesc: '',
    shareClasses: [createEmptyShareClass()],
    allotmentDocFile: null,
    allotmentDocFileCd: null,
  };
}

export function createEmptyShareGroup(): SgShareGroup {
  return { groupName: '', shareholderIndices: [], lines: [] };
}

export function createEmptyConnectedParty(): { name: string; role?: string } {
  return { name: '', role: '' };
}

export function createEmptyOtherPositionHolder(): SgOtherPositionHolder {
  const files = createEmptyPersonIdentityFiles();
  return {
    personCd: files.personCd,
    holderType: 'individual',
    name: '',
    idType: '',
    identificationNo: '',
    nationality: '',
    positions: [],
    registeredLocally: 'YES',
    country: '',
    fbrn: '',
    email: '',
    mobileCountryCode: '',
    mobileAreaCode: '',
    mobileNo: '',
    areaCodeNotApplicable: false,
    address: createEmptySgAddress(),
    nominator: createEmptyNominator(),
    passportFile: null,
    proofOfAddressFile: null,
    passportFileCd: null,
    proofOfAddressFileCd: null,
  };
}

export function createEmptyNominator(): SgNominatorInfo {
  return {
    category: 'individual',
    additionalInfo: '',
    name: '',
    nameAlias: '',
    idType: '',
    identificationNo: '',
    nationality: '',
    birthDate: '',
    registeredLocally: 'YES',
    country: '',
    uen: '',
    fbrn: '',
    corpName: '',
    legalForm: '',
    statute: '',
    registerName: '',
    email: '',
    mobileCountryCode: '',
    mobileAreaCode: '',
    mobileNo: '',
    areaCodeNotApplicable: false,
    address: createEmptySgAddress(),
  };
}

export function createEmptyUbo(): SgUboRow {
  return {
    name: '',
    identificationNo: '',
    ownershipStructure: '',
    residentialAddress: createEmptySgAddress(),
    nationality: '',
    dateOfBirth: '',
    contactNo: '',
    email: '',
    isPep: false,
    pepDetail: createEmptyPep(),
  };
}

export function createEmptyPep(): SgPepRow {
  return {
    category: '',
    relationship: '',
    name: '',
    country: '',
    function: '',
    period: '',
  };
}

export function emptyControllerNoticeMeta() {
  return {
    noticeDate: '',
    noticeRecipientName: '',
    replyToAddress: createEmptySgAddress(),
    noticeSignerName: '',
  };
}

function buildSrvKeys(): { srvA: Record<string, boolean>; srvADetails: Record<string, string> } {
  const srvA: Record<string, boolean> = {};
  const srvADetails: Record<string, string> = {};
  for (let i = 1; i <= 11; i++) {
    srvA[`srv_a_${i}`] = false;
    srvADetails[`srv_a_${i}_details`] = '';
  }
  return { srvA, srvADetails };
}

function buildB1Keys(): Record<string, boolean> {
  const b1Q: Record<string, boolean> = {};
  for (let i = 1; i <= 18; i++) {
    b1Q[`b1_q${i}`] = false;
  }
  return b1Q;
}

function buildB2Keys(): Record<string, boolean> {
  const b2Q: Record<string, boolean> = {};
  for (let i = 1; i <= 5; i++) {
    b2Q[`b2_q${i}`] = false;
  }
  return b2Q;
}

function buildB3Keys(): Record<string, ''> {
  const b3Q: Record<string, ''> = {};
  for (let i = 1; i <= 9; i++) {
    b3Q[`b3_q${i}`] = '';
  }
  return b3Q;
}

export function createSgIncorporationForm(): SgIncorporationForm {
  const { srvA, srvADetails } = buildSrvKeys();
  return {
    applicant: {
      filerLastName: '',
      filerFirstName: '',
      filerEmail: '',
      filerMobileCountryCode: '',
      filerMobileNumber: '',
      filerOfficeCountryCode: '',
      filerOfficeNumber: '',
    },
    company: {
      entityName: '',
      entityFormerNames: '',
      entityUen: '',
      entityRegisteredAddress: createEmptySgAddress(),
      entityPrincipalBusinessAddress: createEmptySgAddress(),
      entityMailingAddress: createEmptySgAddress(),
      entityIncorporationDate: '',
      entityContactPerson: '',
      entityMobileCountryCode: '',
      entityMobileAreaCode: '',
      entityMobileNo: '',
      entityEmail: '',
      entityPrimaryActivityCode: '',
      entityPrimaryActivityName: '',
      entityShareCapital: '',
      entityShareValue: '',
      entityProposedFye: '',
      operatingHourType: 'OVER5',
      operatingHours: '',
      sourceOfWealthInfo: '',
      businessWebsite: '',
      supportingDocumentsNotes: '',
      others: [],
      agentName: '',
      agentIdentificationNo: '',
      agentCompanyName: '',
      agentNationality: '',
      agentResidentialAddress: createEmptySgAddress(),
      agentMobileCountryCode: '',
      agentMobileAreaCode: '',
      agentMobileNo: '',
      agentDateOfBirth: '',
      agentJobTitle: '',
      agentEmail: '',
      managingDirectors: [],
      ceos: [],
      partners: [],
    },
    directors: [createEmptyDirector()],
    includeDirectors: 'no',
    includeSecretary: 'no',
    includeShareholders: 'no',
    includeControllers: 'no',
    includeEngagement: 'no',
    otherPositionHolders: [],
    secretary: {
      secretaryCd: newFileCd(),
      secretaryName: '',
      secretaryAddress: createEmptySgAddress(),
      secretaryIdType: '',
      secretaryIdentificationNo: '',
      secretaryNationality: '',
      secretaryEmail: '',
      secretaryMobileCountryCode: '',
      secretaryMobileAreaCode: '',
      secretaryMobileNo: '',
      secretaryAreaCodeNotApplicable: false,
      appointmentEffectiveDate: '',
      executionDate: '',
      positions: ['LC_SEC'],
      passportFile: null,
      proofOfAddressFile: null,
      passportFileCd: null,
      proofOfAddressFileCd: null,
    },
    shareholders: [createEmptyShareholder()],
    shareStructure: {
      currencies: [createEmptyShareCurrency()],
      groups: [],
    },
    corporateControllers: [],
    individualControllers: [],
    engagement: {
      proposedCompanyName: '',
      currencyCd: '',
      currencySymbol: '',
      cspName: '',
      cspAddressLine1: '',
      cspAddressLine2: '',
      cspPhone: '',
      cspEmail: '',
      cspWebsite: '',
      feeIncorporationPackage: '',
      feeNomineeDirector: '',
      feeNomineeDirectorDeposit: '',
      feeEmploymentPass: '',
      feeCorppassOpening: '',
      feeBankAccountOpening: '',
      feeAnnualSecretarial: '',
      feeRegisteredAddress: '',
      feeAnnualCorppassMaintenance: '',
      feeAnnualNomineeDirector: '',
      feeAnnualXbrl: '',
      acceptanceDate: '',
      employmentPass: 'no',
      financialYearEnd: 'december31',
      financialYearEndDate: '',
    },
    risk: {
      srvA,
      srvADetails,
      b1Q: buildB1Keys(),
      b2Q: buildB2Keys(),
      b3Q: buildB3Keys(),
      riskRatingReasons: '',
      riskRating: '',
      recommendation: '',
      recommendingOfficerName: '',
      recommendingOfficerPosition: '',
      recommendingOfficerDate: '',
      recommendingOfficerSignature: '',
      approval: '',
      approvingOfficerName: '',
      approvingOfficerPosition: '',
      approvingOfficerDate: '',
      approvingOfficerSignature: '',
    },
    declaration: {
      declarantName: '',
      declarantIdentificationNo: '',
      declarationDate: '',
      authorizedPersonName: '',
      authorizedPersonTitle: '',
      certificationDate: '',
      noticeDate: '',
      noticeRecipientName: '',
      replyToAddress: '',
      noticeSignerName: '',
    },
  };
}
